import { useState } from "react";
import { Link } from "react-router-dom";
import { DeviceMobile, DotsThreeVertical, Truck, Package, Camera, Moon, ShieldCheck, Wallet, Copy, Check, DownloadSimple, QrCode } from "@phosphor-icons/react";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { useInstallPrompt } from "@/lib/install";

const SHOTS = [
  {
    src: "https://static.prod-images.emergentagent.com/jobs/816db716-8135-404c-89b1-f60cefaf7f84/images/f9dbad2791b4cedc7c8283cc089bd9bd8e0f2844217a014606266f3ac2e8768d.jpeg",
    caption: "At the gate: the delivery timeline, proof photos and the tracking number.",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/816db716-8135-404c-89b1-f60cefaf7f84/images/0819078839a8f8820de7da616252e4376d9101e8e5816804708606b423331e18.jpeg",
    caption: "On the forklift: scan a bin, put stock away, pick a task and tick it off.",
  },
];

const SOCIAL_TILE = "https://static.prod-images.emergentagent.com/jobs/816db716-8135-404c-89b1-f60cefaf7f84/images/3f2938edd2639d697e48872a0b7acc7704499180d4693e2f6c345257c664e150.jpeg";

const FLOOR = [
  [Package, "Scan and count", "Camera barcode and QR scanning for bins and stock, put-away and pick tasks, ticked off line by line."],
  [Truck, "Runs and windows", "Promised pick-up and delivery windows, status updates, proof photos and driver hours from the cab."],
  [Moon, "Breaks and rest", "Restroom, water, phone-call and nap breaks logged in seconds, plus sleep and a motel on the route."],
];

const MANAGERS = [
  [ShieldCheck, "Decide from anywhere", "Approve purchase orders, answer quotes, assess claims and read incident reports on the phone."],
  [Wallet, "Take and make payment", "Google Pay and cards through Stripe Checkout, with receipts and the full payments ledger."],
  [Camera, "Post and share", "Photos and clips from the floor, shared to WhatsApp, Facebook, X or LinkedIn in one tap."],
];

const CAPTIONS = [
  {
    key: "facebook",
    label: "Facebook",
    text: "If your crew carries an Android phone, they can carry the whole yard. Stock, bin scanning, tool crib, freight windows, safety reports, breaks and payments — one login, installed straight from Chrome. No Play Store download, no training day. Unity & Utility.",
  },
  {
    key: "instagram",
    label: "Instagram",
    text: "Scan the bin. Log the break. Close the run. All from the phone already in your pocket.\n\n#Warehouse #Logistics #Manufacturing #Freight #Forklift #Android #UnityAndUtility",
  },
  {
    key: "x",
    label: "X",
    text: "On Android: bin scanning, stock counts, freight windows, safety reports, breaks and payments. Install it from Chrome in one tap — no Play Store needed.",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    text: "Android is what most of the industrial workforce actually carries, so Unity & Utility installs straight from Chrome as a full-screen app: inventory and bin scanning, purchasing, freight time windows, safety reporting, wellbeing and payments — all usable one-handed, with nothing to roll out through a device management queue.",
  },
];

const Group = ({ title, items, testid }) => (
  <div data-testid={testid}>
    <h3 className="font-display text-lg font-bold tracking-tight text-[#0F172A]">{title}</h3>
    <div className="mt-3 grid gap-3 sm:grid-cols-3">
      {items.map(([Icon, head, body]) => (
        <div key={head} className="border border-border bg-white p-4">
          <Icon size={22} className="text-[#2E7D32]" weight="duotone" />
          <p className="mt-2 text-sm font-semibold text-[#0F172A]">{head}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{body}</p>
        </div>
      ))}
    </div>
  </div>
);

export default function Android() {
  const [copied, setCopied] = useState(null);
  const [note, setNote] = useState(null);
  const { canInstall, installed, install } = useInstallPrompt();

  useSeo({
    title: "Unity & Utility on Android — install it from Chrome | Unity & Utility",
    description: "Bin scanning, stock, purchasing, freight windows, safety, breaks and payments on an Android phone. Install it from Chrome in one tap — no Play Store download.",
    path: "/android",
    image: SOCIAL_TILE,
    jsonLd: breadcrumbs([["Home", "/"], ["Android", "/android"]]),
  });

  const copy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 2000); } catch {}
  };

  const onInstall = async () => {
    const outcome = await install();
    if (outcome === "unavailable") setNote("Your browser did not offer the install prompt — use Chrome's ⋮ menu and choose “Install app” or “Add to Home screen”.");
    else if (outcome === "dismissed") setNote("No problem — you can install it any time from Chrome's ⋮ menu.");
    else setNote(null);
  };

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="android-page">
        <Crumbs items={[["Home", "/"], ["Android", "/android"]]} />
        <p className="overline text-[#2E7D32]">Built for the phone on the floor</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
          <DeviceMobile size={36} weight="duotone" className="mt-1 shrink-0 text-[#2E7D32]" />
          Unity &amp; Utility on Android
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="android-intro">
          Android is what the yard actually carries — so the whole platform runs on it. Chrome installs it as a
          proper full-screen app in one tap, the camera scans bins and barcodes, and every screen is built to be
          used one-handed with a glove on.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="android-actions">
          {installed ? (
            <span data-testid="android-installed" className="border border-[#2E7D32] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-[#2E7D32]">
              <Check size={15} className="mr-1.5 inline align-[-2px]" /> Installed on this device
            </span>
          ) : (
            <button type="button" data-testid="android-install-button" onClick={onInstall}
              className="bg-[#002FA7] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-150 hover:bg-[#00248A]">
              <DownloadSimple size={16} className="mr-1.5 inline align-[-3px]" />
              {canInstall ? "Install the app" : "Install on this phone"}
            </button>
          )}
          <Link to="/register" data-testid="android-cta-register" className="tap-safe text-sm font-semibold text-[#002FA7] hover:underline">
            Create an account
          </Link>
          <Link to="/iphone" data-testid="android-to-iphone" className="tap-safe text-sm font-semibold text-slate-600 hover:text-[#002FA7]">
            On an iPhone instead?
          </Link>
        </div>
        {note && <p className="mt-3 text-sm text-[#B3241B]" data-testid="android-install-note">{note}</p>}
        <div className="mt-4">
          <ShareBar url={`${window.location.origin}/android`}
            text="Unity & Utility installs straight from Chrome on Android — bin scanning, stock, freight, safety and payments."
            testid="android-share" instagram />
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2" data-testid="android-shots">
          {SHOTS.map((s) => (
            <figure key={s.src} className="border border-border bg-white">
              <img src={s.src} alt={s.caption} loading="lazy" className="aspect-[3/4] w-full object-cover" />
              <figcaption className="border-t border-border p-3 text-sm text-slate-600">{s.caption}</figcaption>
            </figure>
          ))}
        </div>

        <section className="mt-12 border border-[#2E7D32] bg-white p-5 sm:p-7" data-testid="android-install-steps">
          <p className="overline text-[#2E7D32]">One tap in Chrome</p>
          <h2 className="font-display mt-1 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Put it on your home screen
          </h2>
          <ol className="mt-4 space-y-4">
            {[
              [DownloadSimple, "Open this page in Chrome and press Install the app above — Chrome will ask once and it is done."],
              [DotsThreeVertical, "No prompt? Tap Chrome's ⋮ menu and choose “Install app” or “Add to Home screen”."],
              [QrCode, "Hand the phone to the next person and let them scan the bin — the camera scanner works inside the installed app."],
            ].map(([Icon, text], i) => (
              <li key={i} className="flex gap-3" data-testid={`android-step-${i + 1}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center bg-[#0F172A] text-sm font-bold text-white">{i + 1}</span>
                <p className="pt-1 text-sm leading-relaxed text-slate-700">
                  <Icon size={16} className="mr-1.5 inline align-[-3px] text-[#2E7D32]" />
                  {text}
                </p>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-xs text-slate-500" data-testid="android-no-playstore">
            This installs the web app itself, not a Play Store download — everyone is always on the current
            version, and there is nothing to push through a device management queue.
          </p>
        </section>

        <div className="mt-12 space-y-8">
          <Group title="If you drive or work the floor" items={FLOOR} testid="android-floor" />
          <Group title="If you buy, sell or run the place" items={MANAGERS} testid="android-managers" />
        </div>

        <section className="mt-12" data-testid="android-promo">
          <p className="overline text-[#B3241B]">Tell your crew and your customers</p>
          <h2 className="font-display mt-1 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Ready-made posts
          </h2>
          <img src={SOCIAL_TILE} alt="Unity & Utility on Android" loading="lazy"
            className="mt-4 w-full border border-border object-cover" data-testid="android-promo-image" />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {CAPTIONS.map((c) => (
              <div key={c.key} className="border border-border bg-white p-4" data-testid={`android-caption-${c.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="overline text-slate-500">{c.label}</p>
                  <button type="button" data-testid={`android-copy-caption-${c.key}`} onClick={() => copy(c.key, c.text)}
                    className="tap-safe text-xs font-semibold text-[#002FA7]">
                    {copied === c.key
                      ? <><Check size={13} className="mr-1 inline align-[-2px]" />Copied</>
                      : <><Copy size={13} className="mr-1 inline align-[-2px]" />Copy</>}
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">{c.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Save the picture, copy a caption and post it — Facebook and Instagram do not allow posting on your
            behalf without a reviewed business app.
          </p>
        </section>
      </div>
    </PublicShell>
  );
}
